import React, { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, UploadCloud } from 'lucide-react';
import api from '../services/api';
import Modal from './Modal';

// ============================================================
// Carga de factura de proveedor por IA (#133).
//
// <BotonFotoFactura/>: ícono de cámara que abre la cámara/galería (o un
// PDF), sube el archivo a /compras/extraer-ia y devuelve la propuesta de
// la IA. No guarda nada: el usuario revisa todo en <FacturaForm/>.
// Se usa en la toolbar de Proveedores y en la del estado de cuenta.
// <ZonaArchivoFactura/>: lo mismo como zona de arrastrar y soltar (en
// "Nueva factura de compra" del Inicio).
//
// <ConfirmarProveedorIA/>: si el RIF leído no es de ningún proveedor de la
// empresa, primero ofrece los parecidos (RIF casi igual o nombre parecido,
// `proveedores_parecidos`); si no es ninguno, muestra los datos leídos para
// que el usuario los corrija y confirme el alta antes de crearlo.
// ============================================================

const ACEPTA = 'image/*,application/pdf,.heic,.heif';

function esArchivoValido(archivo) {
  return /^image\//.test(archivo.type) || archivo.type === 'application/pdf' || /\.(heic|heif|pdf)$/i.test(archivo.name);
}

// Sube el archivo a /compras/extraer-ia y entrega la propuesta de la IA.
// `aviso` es la capa "Leyendo la factura…" mientras espera.
function useLeerFactura(onResultado) {
  const [leyendo, setLeyendo] = useState(false);

  async function leer(archivo) {
    if (!archivo) return;
    if (!esArchivoValido(archivo)) {
      alert('Ese archivo no es una foto ni un PDF. Usa una imagen (JPG, PNG, HEIC) o un PDF de la factura.');
      return;
    }
    const datos = new FormData();
    datos.append('archivo', archivo);
    setLeyendo(true);
    try {
      const { data } = await api.post('/compras/extraer-ia', datos);
      if (!data.datos) {
        alert(data.error_ia || 'No se pudo leer la factura automáticamente.');
        return;
      }
      onResultado(data);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo subir la factura');
    } finally {
      setLeyendo(false);
    }
  }

  const aviso = leyendo && (
    <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg px-6 py-5 flex items-center gap-3 text-slate-700">
        <Loader2 size={20} className="animate-spin text-teal-600" />
        <span className="text-sm">Leyendo la factura…</span>
      </div>
    </div>
  );

  return { leyendo, leer, aviso };
}

// Con `children` el botón lleva texto además del ícono (y `className`
// reemplaza su estilo); sin él es el botón de ícono de siempre.
export function BotonFotoFactura({ onResultado, className = '', children }) {
  const inputRef = useRef(null);
  const { leyendo, leer, aviso } = useLeerFactura(onResultado);

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={leyendo}
        title="Cargar factura desde foto (IA)"
        className={children ? className : `bg-teal-600 hover:bg-teal-700 text-white p-2 rounded-md disabled:opacity-50 ${className}`}
      >
        <Camera size={18} />
        {children}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACEPTA}
        onChange={(e) => { const archivo = e.target.files?.[0]; e.target.value = ''; leer(archivo); }}
        className="hidden"
      />
      {aviso}
    </>
  );
}

// <ZonaArchivoFactura/>: espacio para arrastrar y soltar la foto o el PDF
// de la factura (o pulsarlo para elegirlo / tomar la foto). Mismo flujo de
// lectura por IA que <BotonFotoFactura/>.
export function ZonaArchivoFactura({ onResultado }) {
  const inputRef = useRef(null);
  const [encima, setEncima] = useState(false);
  const { leyendo, leer, aviso } = useLeerFactura(onResultado);

  // Si el archivo cae fuera de la zona, que el navegador no lo abra y saque
  // al usuario de la app.
  useEffect(() => {
    const evitar = (e) => e.preventDefault();
    window.addEventListener('dragover', evitar);
    window.addEventListener('drop', evitar);
    return () => { window.removeEventListener('dragover', evitar); window.removeEventListener('drop', evitar); };
  }, []);

  function soltar(e) {
    e.preventDefault();
    setEncima(false);
    if (!leyendo) leer(e.dataTransfer.files?.[0]);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setEncima(true); }}
        onDragEnter={(e) => { e.preventDefault(); setEncima(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setEncima(false); }}
        onDrop={soltar}
        disabled={leyendo}
        className={`w-full flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-4 py-6 text-center transition disabled:opacity-50 ${
          encima ? 'border-teal-500 bg-teal-50' : 'border-slate-300 bg-slate-50 hover:border-teal-400 hover:bg-teal-50/50'
        }`}
      >
        <UploadCloud size={28} className={encima ? 'text-teal-600' : 'text-slate-400'} />
        <span className="text-sm font-semibold text-slate-700">
          {encima ? 'Suelta el archivo para leerlo' : 'Arrastra aquí la factura'}
        </span>
        <span className="text-xs text-slate-500">
          o <span className="font-medium text-teal-700">pulsa para elegirla</span> (foto o PDF). La IA la lee y tú la revisas.
        </span>
      </button>
      <input ref={inputRef} type="file" accept={ACEPTA} onChange={(e) => { const archivo = e.target.files?.[0]; e.target.value = ''; leer(archivo); }} className="hidden" />
      {aviso}
    </>
  );
}

export function ConfirmarProveedorIA({ extraccion, proveedorActual = null, onCreado, onUsarActual, onUsarProveedor, onCancelar }) {
  const p = extraccion?.datos?.proveedor || {};
  const parecidos = extraccion?.proveedores_parecidos || [];
  const [crearNuevo, setCrearNuevo] = useState(parecidos.length === 0);
  const [form, setForm] = useState({
    nombre: p.razon_social || '',
    rif: p.rif || '',
    direccion: p.direccion || '',
    telefono: p.telefono || '',
    email: p.email || '',
    dias_credito_default: extraccion?.datos?.dias_credito ?? ''
  });
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function guardar(e) {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      const { data } = await api.post('/proveedores', form);
      onCreado(data.id);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo crear el proveedor');
      setGuardando(false);
    }
  }

  const campo = (clave, placeholder, extra = {}) => (
    <input
      value={form[clave]}
      placeholder={placeholder}
      onChange={(e) => setForm({ ...form, [clave]: e.target.value })}
      className="border rounded-md px-3 py-2 w-full text-sm"
      {...extra}
    />
  );

  // Primero los proveedores parecidos que ya existen: crear uno nuevo es la
  // última opción, para no repetir proveedores.
  if (!crearNuevo) {
    return (
      <Modal title="¿Es alguno de estos proveedores?" onClose={onCancelar} maxWidth="max-w-lg">
        <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <p className="text-xs text-slate-500">La IA leyó en la factura</p>
          <p className="font-medium text-slate-700">{p.razon_social || 'Sin nombre'}</p>
          {p.rif && <p className="text-xs text-slate-500">RIF {p.rif}</p>}
        </div>
        <p className="mt-3 mb-2 text-xs text-slate-500">
          No hay ningún proveedor con ese RIF exacto, pero estos se parecen. Elige el correcto o crea uno nuevo.
        </p>
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {parecidos.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onUsarProveedor(c)}
              className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-slate-50"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-slate-700">
                  {c.nombre}
                  {!c.activo && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-normal text-slate-500">Inhabilitado</span>}
                </span>
                <span className="block text-xs text-slate-400">
                  {c.rif ? `RIF ${c.rif}` : 'Sin RIF'} · {c.motivo}
                </span>
              </span>
              <span className="shrink-0 text-xs font-medium text-teal-700">Usar este</span>
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onCancelar} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100">
            Cancelar
          </button>
          <button type="button" onClick={() => setCrearNuevo(true)} className="px-4 py-2 rounded-md text-sm text-slate-600 border hover:bg-slate-50">
            Ninguno: crear proveedor nuevo
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Proveedor nuevo" onClose={onCancelar} maxWidth="max-w-lg">
      <form onSubmit={guardar} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <p className="sm:col-span-2 text-xs text-slate-500">
          El RIF de la factura no corresponde a ningún proveedor de esta empresa. Revisá los datos que leyó la IA y confirmá para crearlo.
        </p>
        <div className="sm:col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Razón social</label>
          {campo('nombre', 'Nombre del proveedor', { required: true })}
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">RIF</label>
          {campo('rif', 'J-12345678-9')}
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Teléfono</label>
          {campo('telefono', 'Opcional')}
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Dirección fiscal</label>
          {campo('direccion', 'Opcional')}
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Email</label>
          {campo('email', 'Para enviarle los comprobantes de retención', { type: 'email' })}
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Días de crédito por defecto</label>
          {campo('dias_credito_default', 'Opcional', { type: 'number', min: '0' })}
        </div>
        {error && <p className="text-red-600 text-sm sm:col-span-2">{error}</p>}
        <div className="sm:col-span-2 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onCancelar} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100">
            Cancelar
          </button>
          {proveedorActual && (
            <button type="button" onClick={onUsarActual} className="px-4 py-2 rounded-md text-sm text-slate-600 border hover:bg-slate-50">
              Cargarla en {proveedorActual.nombre}
            </button>
          )}
          <button type="submit" disabled={guardando} className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700 disabled:opacity-60">
            Crear proveedor y seguir
          </button>
        </div>
      </form>
    </Modal>
  );
}
